import Button from '@atlaskit/button/standard-button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { FormattedDate, Icon, OpenSidebarPayload, useSidebar } from '@css-xp/base';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { OfferingType } from '@atlassian/cssxp-ccp-types';
import { Maybe, TransactionAccount } from '@css-xp/graphtypes';
import {
  BillingStatus,
  EntitlementDetails,
  InvoiceGroup,
  OrderItemRequestType,
  Statuspage,
  LinkedDestinationEntitlement,
  SubscriptionScheduleAction,
  LinkedEntitlementTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { isFreePlanEntitlement } from '@atlassian/cssxp-utils-entitlement';
import { memoize } from 'lodash';
import moment from 'moment';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { SIDEBAR } from '../..';
import {
  CANCELLED_LINKED_ENT_DESCRIPTION,
  CANCELLED_LINKED_ENT_HEADER,
  COBILL_WARNING_HEADER,
  CONFIRM_OK_LABEL,
  LINKED_SUBSCRIPTION_DESCRIPTION,
  PREDICATES,
  SWITCH_BILING_PROFILE_WARNING_HEADER,
  TRANSFER_TXA_WARNING_HEADER,
  UNDO_SCHEDULED_CHANGES_DESCRIPTION,
  UNDO_SCHEDULED_CHANGES_HEADER,
  VIEW_SUBSCRIPTION_ON_NEXT_SCREEN,
} from '../../../common/constants';
import { isTrialEntitlement } from '../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';

export const getStatuspageHeaderText = (entitlement: EntitlementDetails) => {
  // temporary fix as Hydra data is being populated correctly
  let cloudSiteHostnameWithoutHttp = '';
  if (entitlement?.parentSen) {
    cloudSiteHostnameWithoutHttp = (entitlement?.parentCloudSiteName ?? '')
      .replace('https://', '')
      .replace('http://', '');
  } else {
    cloudSiteHostnameWithoutHttp = (entitlement.cloudSiteHostname ?? '').replace('https://', '').replace('http://', '');
  }

  return cloudSiteHostnameWithoutHttp;
};

export const StatuspageSubheaderText = ({ entitlement }: { entitlement: EntitlementDetails }) => {
  // const isFinancePersonPermission: boolean = useSelector(isFinancePerson); TO:DO
  const isFinancePersonPermission = true;
  if (!entitlement.entitlementNumber) {
    return <>{entitlement.id}</>;
  }
  if (isFinancePersonPermission) {
    return <>{entitlement.entitlementNumber + ' | ' + entitlement.id}</>;
  }
  return <>{entitlement.entitlementNumber}</>;
};

export const getContactFullName = memoize((fullName?: string | null) => {
  return fullName ? fullName : '';
});

export const getEmail = memoize((email?: string | null) => {
  return email ? email : 'N/A';
});

export const getDateString = memoize((date: string): string => {
  return date ? moment(new Date(Number(date))).toString() : 'Not available';
});

export const CheckPaymentIssue = (entitlement: EntitlementDetails, transactionAccount: TransactionAccount): boolean => {
  const activeBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement.billingGroupId;
  let invoiceGroup = null;
  if (!activeBillingGroupId) {
    invoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes[0] ?? null;
  } else {
    invoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes.find(
      (invoiceGroup) => activeBillingGroupId === invoiceGroup?.id
    );
  }
  const isPaymentIssue = invoiceGroup?.defaultPaymentMethod?.paymentIssue || false;
  return isPaymentIssue;
};

export const WarningModal = ({ isOpen, onClose, payload }: { isOpen: boolean; onClose: () => void; payload: any }) => {
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const { openSidebar } = useSidebar();
  const history = useHistory();
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const { data, getTransactionAccount } = transactionAccountContext;
  const transactionAccount = getTransactionAccount(data);

  const linkedInvoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes?.find(
    (invoiceGroup) => invoiceGroup?.id === payload?.entitlement?.billingGroupId
  );

  const hasShipToParty = linkedInvoiceGroup?.shipToParty?.id ? true : false;
  const hasDefaultPaymentMethod = linkedInvoiceGroup?.defaultPaymentMethod ? true : false;
  const isFreeEntitlement = validateFreeSubscriptions(payload?.entitlement);

  const hasBillToParty =
    !isFreeEntitlement && !hasDefaultPaymentMethod ? true : transactionAccount?.billToParty ? true : false;

  const createBillingProfileURL = `${TRANSACTION_ACCOUNT_PATH_2}/${transactionAccount?.id}/?inner_tab=billing-profiles#account-details`;

  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={onClose}>
          <ModalHeader>
            <ModalTitle>
              <Icon type="warning" />{' '}
              <span style={{ marginLeft: '24px' }}>
                {linkedInvoiceGroup || isFreeEntitlement
                  ? hasBillToParty
                    ? 'Billing profile needs updating first'
                    : 'Update billing account'
                  : 'You need to add a billing profile first'}
              </span>
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div>
              <p>Subscription changes can’t be made because there’s:</p>
              <br />
              <ul>
                {linkedInvoiceGroup ? (
                  <>
                    {!hasDefaultPaymentMethod ? <li>No payment method available for the billing profile</li> : null}
                    {!hasBillToParty ? <li>No bill-to address available for the billing account</li> : null}
                  </>
                ) : (
                  <>
                    {!linkedInvoiceGroup ? (
                      isFreeEntitlement && !hasBillToParty ? (
                        <li>No bill-to address available for the billing account</li>
                      ) : (
                        <li>No billing profile available</li>
                      )
                    ) : null}
                  </>
                )}
              </ul>
              <br />
              <p>
                <>{`Add the information${hasBillToParty ? ' or switch the billing profile' : ''}`}</>, then try again.
                Still having trouble? Contact the <strong>#help-advocate-central</strong> channel.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} appearance="subtle">
              Cancel
            </Button>
            <Button
              onClick={() => {
                onClose();
                linkedInvoiceGroup || isFreeEntitlement
                  ? hasBillToParty
                    ? handleSidebarAction({
                        sidebarType: SIDEBAR.SET_INVOICE_GROUP,
                        payload: {
                          ...payload,
                          transactionAccountContext,
                        },
                        sidebarOptions: {
                          isOverlay: true,
                          sidebarWidth: '600px',
                        },
                      })
                    : history.push(`${TRANSACTION_ACCOUNT_PATH_2}/${transactionAccount?.id}`)
                  : window.open(createBillingProfileURL, '_blank');
              }}
              appearance="primary"
            >
              {linkedInvoiceGroup || isFreeEntitlement
                ? hasBillToParty
                  ? 'Update billing profile'
                  : 'Update billing account'
                : 'Add billing profile'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};

export const isAppEntitlement = (entOfferingType: OfferingType): boolean => {
  return entOfferingType === OfferingType.CHILD;
};

// for validating if IG exists and it has payment method and ship-to-address.
export const validateBillingProfile = (entitlementBillingGroupId: string, availableInvoiceGroups: InvoiceGroup[]) => {
  const entitlementLinkedIG: InvoiceGroup[] = availableInvoiceGroups.filter(
    (ig) => ig.id === entitlementBillingGroupId
  );
  return entitlementLinkedIG.length > 0 && entitlementLinkedIG[0].defaultPaymentMethod;
};

export const validateBillTo = (txa: Maybe<TransactionAccount>): boolean => {
  return txa?.billToParty ? true : false;
};

export const validateFreeSubscriptions = (entitlement: EntitlementDetails) => {
  return (
    entitlement?.edition?.toLowerCase().includes('free') ||
    (entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE &&
      entitlement.edition?.toLowerCase().includes('public trial')) ||
    (entitlement.baseProduct === EntitlementBaseProduct.ATLAS && entitlement.edition?.toLowerCase().includes('beta'))
  );
};

// Firstly validates if its a subscription for which IG is not present for eg: Free & Public trial .
// Secondly if above case is false then validates if IG attached is having peyment method and ship-to-address.
export const validateSubscriptionDetails = (
  entitlement: EntitlementDetails,
  availableInvoiceGroups: InvoiceGroup[],
  transactionAccount: Maybe<TransactionAccount>
) => {
  let unlimitedDiscountApplied = false;

  entitlement?.promotions?.forEach((promotion) => {
    const benefitsObject = promotion?.benefits?.[0];

    if (
      benefitsObject?.benefitType === 'DISCOUNT' &&
      benefitsObject?.duration === 'FOREVER' &&
      benefitsObject?.subBenefitType === 'PERCENTAGE' &&
      benefitsObject?.value === 100
    ) {
      unlimitedDiscountApplied = true;
    }
  });

  if (unlimitedDiscountApplied) {
    return true;
  }

  const validateBillToAdress = validateBillTo(transactionAccount);
  const isTrialSubscription = isTrialEntitlement(
    entitlement,
    entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE
  );
  if (availableInvoiceGroups?.length > 0 && (isFreePlanEntitlement(entitlement) || isTrialSubscription)) {
    return true;
  }
  return (
    validateBillingProfile(
      entitlement.activeSubscriptionBillingGroupId ?? (entitlement?.billingGroupId as string),
      availableInvoiceGroups
    ) && validateBillToAdress
  );
};

export const getWarningDailogProps = (
  header: string,
  description1: string,
  description2: string = VIEW_SUBSCRIPTION_ON_NEXT_SCREEN,
  okLabel: string = CONFIRM_OK_LABEL
) => ({
  header: header,
  description: (
    <div>
      <p>{description1}</p>
      <br></br>
      <p>{description2}</p>
    </div>
  ),
  okLabel: okLabel,
});

export const getSidebarProps = (sidebarType: string, payload: any, sidebarWidth: string) => {
  return {
    sidebar: sidebarType,
    payload: payload,
    sidebarOptions: {
      isOverlay: true,
      sidebarWidth: sidebarWidth,
    },
  };
};

export const getChangeBillDateSidebarPayload = (
  entitlement: EntitlementDetails,
  refetch: () => void,
  isAddOnChangesEnabled?: boolean,
  isSandboxSupportForSubscriptionsEnabled?: boolean,
  isSandboxDecouplingEnabled?: boolean
  // eslint-disable-next-line max-params
) => {
  return getSidebarProps(
    SIDEBAR.SET_BILL_DATE,
    {
      invoiceGroupId: entitlement?.billingGroupId,
      product: entitlement,
      refetch,
      isMetaDataHidden: true,
      isAddOnChangesEnabled,
      isSandboxSupportForSubscriptionsEnabled,
      isSandboxDecouplingEnabled,
    },
    '60%'
  );
};

export const getSwitchBillingProfilePayload = (
  entitlement: EntitlementDetails,
  transactionAccountContext: any,
  refetch: () => void
) => {
  return getSidebarProps(
    SIDEBAR.SET_INVOICE_GROUP,
    {
      entitlement,
      transactionAccountContext,
      refetch,
    },
    '600px'
  );
};

export const getSwitchTxaPayload = ({
  entitlement,
  transactionAccount,
  invoiceGroup,
  refetch,
}: {
  entitlement: EntitlementDetails;
  transactionAccount: any;
  invoiceGroup: any;
  refetch: () => void;
}) => {
  return getSidebarProps(
    SIDEBAR.TRANSFER_ENTITLEMENT_TO_TRANSACTION_ACCOUNT,
    {
      entitlement,
      transactionAccount,
      invoiceGroup,
      refetch,
    },
    '1000px'
  );
};

export const undoChangePayload = (entitlement: EntitlementDetails | undefined) => {
  if (!entitlement) return null;
  return {
    changeAction: entitlement.scheduledChanges?.subscriptionScheduleAction,
    changeDate: entitlement.scheduledChanges?.nextChangeTimestamp,
    oldValues: {
      billingFrequency: entitlement.pricingPlan?.billingFrequency || null,
      offering: entitlement.offering?.name || null,
      pricingPlan: entitlement.pricingPlan?.type,
    },
    newValues: {
      billingFrequency: entitlement.scheduledChanges?.pricingPlan?.billingFrequency || null,
      offering: entitlement.scheduledChanges?.offering?.name || null,
      pricingPlan: entitlement.scheduledChanges?.pricingPlan?.type || null,
    },
    scheduledPromotions: entitlement.scheduledChanges?.promotions,
    entitlementNumber: entitlement?.entitlementNumber,
    entitlementId: entitlement?.id,
    scheduledChanges: entitlement?.scheduledChanges,
    pricingPlan: entitlement?.pricingPlan,
    offering: entitlement?.offering,
    productId: entitlement.productId,
    productName: entitlement.productName,
    units: entitlement.unitCount,
    scheduledQuantity: entitlement.scheduledChanges?.chargeQuantities ?? [],
    promotions: entitlement.promotions ?? [],
    entitlement,
  };
};

export const getUndoScheduleChangePayload = (entitlement: EntitlementDetails | undefined, refetch: () => void) => {
  const payload = { ...undoChangePayload(entitlement), refetch };
  return getSidebarProps(SIDEBAR.CANCEL_SCHEDULED_BILLING_CHANGES, payload, '600px');
};

export function fetchWarningProps(type: string) {
  let description;
  switch (type) {
    case PREDICATES.SET_BILL_DATE:
      description = LINKED_SUBSCRIPTION_DESCRIPTION('bill date');
      return getWarningDailogProps(COBILL_WARNING_HEADER, description);

    case PREDICATES.UPDATE_INVOICE_GROUP:
      description = LINKED_SUBSCRIPTION_DESCRIPTION('billing profile');
      return getWarningDailogProps(SWITCH_BILING_PROFILE_WARNING_HEADER, description);

    case PREDICATES.TRANSFER_BILLING_ACCOUNT:
      description = LINKED_SUBSCRIPTION_DESCRIPTION('billing account');
      return getWarningDailogProps(TRANSFER_TXA_WARNING_HEADER, description);

    case PREDICATES.CANCEL_OR_REACTIVATE_ENTITLEMENT:
      return getWarningDailogProps(CANCELLED_LINKED_ENT_HEADER, CANCELLED_LINKED_ENT_DESCRIPTION, '');

    case PREDICATES.CANCEL_SCHEDULED_SUBSCRIPTION:
      return getWarningDailogProps(UNDO_SCHEDULED_CHANGES_HEADER, UNDO_SCHEDULED_CHANGES_DESCRIPTION);
  }
}

export const getLatestPeriodEndDate = (
  billStatus: Statuspage['billingStatus'],
  latestPeriodEndDate: Statuspage['latestPeriodEndDate']
) => {
  const billingStatus = billStatus?.toUpperCase();
  if (billingStatus === BillingStatus.ACTIVE) return <FormattedDate date={latestPeriodEndDate} />;
  return <span className="data-unavailable">N/A</span>;
};

//This check is need for the time being until CCP supports scheduled operations
export const getFormError = (errorMsg: string, orderType?: string): React.ReactElement | string => {
  if (
    (orderType === OrderItemRequestType.CANCELLATION_ORDER || orderType === OrderItemRequestType.AMENDMENT_ORDER) &&
    errorMsg.includes('Entitlement relationship validation failed')
  ) {
    return (
      <>
        {`This subscription can't be ${
          orderType === OrderItemRequestType.CANCELLATION_ORDER ? 'canceled' : 'changed'
        } just yet. You need to cancel all linked items to this subscription first.`}
        <br />
        {errorMsg}
      </>
    );
  }
  return errorMsg;
};

export const isActiveEntitlementWithoutScheduleCancellation = (
  linkedEntitlements: LinkedDestinationEntitlement[] | undefined | null
) => {
  if (linkedEntitlements && linkedEntitlements?.length > 0) {
    return linkedEntitlements?.some((item) => {
      const scheduledAction = item?.scheduledChanges?.subscriptionScheduleAction;
      return (
        item?.billingStatus === BillingStatus.ACTIVE &&
        scheduledAction !== SubscriptionScheduleAction.CANCEL &&
        scheduledAction !== SubscriptionScheduleAction.UPDATE_CANCEL
      );
    });
  }

  return false;
};
